/**
 * Scorecards Component
 * Displays physician performance metrics in table format (matching AngularJS original)
 */

import { Card, Spin, Alert, Table, Typography } from 'antd';
import { useAppSelector } from '../../../store/hooks';

const { Text } = Typography;

interface ScorecardsProps {
  className?: string;
}

function Scorecards({ className }: ScorecardsProps) {
  const { scorecards, loading, error } = useAppSelector(state => state.dashboard);


  if (loading.scorecards) {
    return (
      <Card className={className}>
        <Spin size="large" tip="Loading scorecards...">
          <div style={{ height: '200px' }} />
        </Spin>
      </Card>
    );
  }

  if (error.scorecards) {
    return (
      <Card className={className}>
        <Alert
          message="Scorecard Error"
          description={error.scorecards}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (!scorecards || !scorecards.scores || scorecards.scores.length === 0) {
    return (
      <Card className={className}>
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          color: '#666' 
        }}>
          <p>No scorecard data available for the selected criteria</p>
        </div>
      </Card>
    );
  }


  // Create table columns dynamically based on scorecard templates
  const columns = [
    {
      title: 'Doctor Name',
      dataIndex: 'physician',
      key: 'physician',
      width: 200,
      render: (text: string) => (
        <Text style={{ fontWeight: 'bold' }}>{text}</Text>
      )
    },
    ...scorecards.scorecard_templates.map((template) => ({
      title: (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
            {template.name}
          </div>
          <div style={{ fontSize: '10px', color: '#666', fontWeight: 'normal' }}>
            Target: {template.target}%
          </div>
        </div>
      ),
      dataIndex: `template_${template.template_id}`,
      key: `template_${template.template_id}`,
      align: 'center' as const,
      width: 120,
      render: (percentage: number | null) => {
        if (percentage === null || percentage === undefined) {
          return <Text type="secondary">0%</Text>;
        }

        // Determine color based on performance vs target
        let color = '#52c41a'; // green - good
        if (percentage < 70) {
          color = '#ff4d4f'; // red - poor
        } else if (percentage < 85) {
          color = '#faad14'; // yellow - needs improvement
        }

        return (
          <Text style={{ 
            color,
            fontWeight: 'bold',
            fontSize: '13px'
          }}>
            {percentage.toFixed(1)}%
          </Text>
        );
      }
    }))
  ];

  // Transform data for table
  const dataSource = scorecards.scores.map((physicianScore, index) => {
    const row: any = {
      key: index,
      physician: physicianScore.physician,
    };

    // Add each scorecard attribute to the row
    physicianScore.scorecard_attributes.forEach((attr) => {
      row[`template_${attr.template_id}`] = attr.average;
    });

    return row;
  });

  return (
    <Card 
      className={className} 
      style={{ marginTop: '20px' }}
    >
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        bordered
        style={{
          fontSize: '12px'
        }}
        scroll={{ x: true }}
      />
    </Card>
  );
}

export default Scorecards;